// Simplified ConfirmationAlert.jsx
import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { FaExclamationTriangle, FaCheck, FaTimes } from "react-icons/fa";
import { arClass } from "../utils/useArClass";
import { useSelector } from "react-redux";

const ConfirmationAlert = ({ alert, message, onConfirm, onCancel, darkMode }) => {
    const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
    const isRTL = defaultLanguage === "ar";
    const [isAnimating, setIsAnimating] = useState(false);
    const [actionType, setActionType] = useState(null);

    const handleCancel = () => {
        setActionType("cancel");
        setIsAnimating(true);
        
        setTimeout(() => {
            onCancel();
        }, 300);
    };

    const handleConfirm = () => {
        setActionType("confirm");
        setIsAnimating(true);
        
        setTimeout(() => {
            onConfirm();
        }, 300);
    };

    const getAlertStyles = () => {
        const baseStyles = defaultLanguage === "ar"
            ? "rounded-xl border-r-4 shadow-lg backdrop-blur-sm"
            : "rounded-xl border-l-4 shadow-lg backdrop-blur-sm";
        
        if (darkMode) {
            return `${baseStyles} bg-amber-950/90 border-amber-500 text-amber-100`;
        } else {
            return `${baseStyles} bg-amber-100/90 border-amber-500 text-amber-800`;
        }
    };

    return (
        <motion.div
            key={alert.id}
            initial={{ opacity: 0, x: isRTL ? -300 : 300, scale: 0.9 }}
            animate={{ 
                opacity: 1, 
                x: 0, 
                scale: 1,
                ...(isAnimating && {
                    opacity: 0,
                    x: actionType === "cancel" ? (isRTL ? -300 : 300) : (isRTL ? 300 : -300),
                    scale: 0.9
                })
            }}
            transition={{ 
                type: "spring", 
                damping: 25, 
                stiffness: 300,
                duration: 0.3 
            }}
            className={`relative overflow-hidden ${getAlertStyles()}`}
        >
            <div className={arClass("p-4 max-sm:p-3")}>
                <div className={arClass("mt-0.5 w-full flex items-center justify-end mb-2")}>
                        <AlertCircle className={arClass("w-5 h-5 text-amber-500 flex-shrink-0")} />
                    </div>
                <div className={arClass("flex items-start gap-3")}>
                    
                    
                    <div className={arClass("flex-1 min-w-0")}>
                        <h3 className={arClass(`font-semibold text-sm leading-tight ${
                            darkMode ? 'text-white' : 'text-gray-900'
                        }`)}>
                            {alert.title || "Confirm Action"}
                        </h3>
                        <p className={arClass(`mt-1 text-sm leading-relaxed max-sm:text-xs ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`)}>
                            {message}
                        </p>
                        
                        <div className={arClass("mt-4 flex gap-2")}>
                            <motion.button
                                animate={{
                                    opacity: isAnimating && actionType === "confirm" ? 0.5 : 1,
                                    scale: isAnimating && actionType === "confirm" ? 0.95 : 1
                                }}
                                transition={{ duration: 0.2 }}
                                onClick={handleCancel}
                                className={arClass(`flex-1 py-2 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 max-sm:text-xs ${
                                    darkMode 
                                        ? 'bg-red-900/50 hover:bg-red-800 text-red-100' 
                                        : 'bg-red-300 hover:bg-red-200 text-red-800'
                                }`)}
                            >
                                <FaTimes className="w-4 h-4" />
                                {
                                    defaultLanguage === "en" ? "Cancel" :
                                    defaultLanguage === "fr" ? "Annuler" :
                                    defaultLanguage === "ar" ? "إلغاء" :
                                    "Cancel"
                                }
                            </motion.button>
                            
                            <motion.button
                                animate={{
                                    opacity: isAnimating && actionType === "cancel" ? 0.5 : 1,
                                    scale: isAnimating && actionType === "cancel" ? 0.95 : 1
                                }}
                                transition={{ duration: 0.2 }}
                                onClick={handleConfirm}
                                className={arClass(`flex-1 py-2 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 max-sm:text-xs ${
                                    darkMode 
                                        ? 'bg-amber-700 hover:bg-amber-600 text-white' 
                                        : 'bg-amber-500 hover:bg-amber-600 text-white'
                                }`)}
                            >
                                <FaCheck className="w-4 h-4" />
                                {
                                    defaultLanguage === "en" ? "Confirm" :
                                    defaultLanguage === "fr" ? "Confirmer" :
                                    defaultLanguage === "ar" ? "تأكيد" :
                                    "Confirm"
                                }
                            </motion.button>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ConfirmationAlert;